/**
 * The Canvas HTTP client: one typed function per endpoint, over a single
 * request core.
 *
 * MSW intercepts at the network layer, so in mock mode this exact code runs —
 * there is deliberately no `MockCanvasClient` class, because a swapped-in fake
 * would leave the real fetch path untested (brainstorm.md 1.13).
 *
 * Three things in here are load-bearing and easy to get wrong:
 *
 *  1. `assertWriteAllowed` — the runtime guard from brainstorm.md 1.19. Read it
 *     before changing anything about how requests are dispatched.
 *  2. Every list endpoint goes through `getList`, which follows `Link` headers.
 *     A list endpoint that does not is a silent-truncation bug waiting to
 *     happen. See pagination.ts.
 *  3. `submitFileUpload` is a three-step dance whose second step returns a
 *     redirect that must be followed, or the file silently fails to attach.
 *     See upload.ts.
 */
import { getConfig, getDevToken, isMockTarget, type CanvasConfig } from './config';
import {
  CanvasHttpError,
  CanvasNetworkError,
  CanvasNoTokenError,
  LiveWriteBlockedError,
} from './errors';
import { RateLimitTracker, fetchAllPages, parseLinkHeader, type PageResult } from './pagination';
import { getCachedToken } from './tokenStore';
import type {
  CanvasActivityItem,
  CanvasAssignment,
  CanvasAssignmentGroup,
  CanvasCalendarEvent,
  CanvasCourse,
  CanvasDiscussionEntry,
  CanvasDiscussionTopic,
  CanvasEnrollment,
  CanvasErrorBody,
  CanvasFile,
  CanvasFolder,
  CanvasId,
  CanvasModule,
  CanvasModuleItem,
  CanvasPage,
  CanvasPlannerItem,
  CanvasQuiz,
  CanvasSubmission,
  CanvasTodoItem,
  CanvasUser,
} from './types';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Canvas's max. Requesting fewer just means more round trips. */
const DEFAULT_PER_PAGE = 100;

export const rateLimit = new RateLimitTracker();

/**
 * Placeholder bearer token used in mock mode so `npm run dev` and the
 * screenshot run work with zero setup.
 *
 * It is deliberately named to be unmistakable if it ever shows up in a log or
 * a screenshot. MSW ignores it; it never leaves the machine.
 */
export const MOCK_TOKEN = 'FAKE_TOKEN_DO_NOT_USE';

/**
 * Which token to send, in priority order:
 *   1. the one the user pasted into Settings (IndexedDB)
 *   2. `VITE_DEV_TOKEN`, and only when `import.meta.env.DEV` (brainstorm.md 1.15)
 *   3. the obvious fake, in mock mode only
 *
 * In live mode with nothing stored this returns null and the caller throws
 * `CanvasNoTokenError`, which the UI turns into "add a token in Settings"
 * rather than a mystery failure.
 */
function resolveToken(): string | null {
  const stored = getCachedToken();
  if (stored) return stored;

  const devToken = getDevToken();
  if (devToken) return devToken;

  if (getConfig().mode === 'mock') return MOCK_TOKEN;

  return null;
}

/* ------------------------------------------------------------------ */
/* THE GUARD                                                           */
/* ------------------------------------------------------------------ */

/**
 * ############################################################
 * Refuse to send a mutating request to anything that is not the mock.
 *
 * This throws BEFORE the request is constructed, so there is no window
 * in which a live write exists in flight. It is not a warning, not a
 * console message, and not skippable by passing a flag at the call
 * site — the only thing that disables it is `allowLiveWrites` in the
 * resolved config, which is `false` throughout this codebase and in
 * every test, and which no application code sets.
 *
 * Why a guard and not just a rule: the target account belongs to a real
 * student. Grades and submissions in it are real and some actions are
 * irreversible. Prompts are advisory; a thrown exception is not
 * (brainstorm.md 1.19).
 * ############################################################
 */
export function assertWriteAllowed(
  method: string,
  url: string,
  config: CanvasConfig = getConfig(),
): void {
  const upper = method.toUpperCase();
  if (!MUTATING_METHODS.has(upper)) return;
  if (isMockTarget(url, config)) return;
  if (config.allowLiveWrites) return;
  throw new LiveWriteBlockedError(upper, url);
}

/* ------------------------------------------------------------------ */
/* Request core                                                        */
/* ------------------------------------------------------------------ */

export interface RequestOptions {
  method?: string;
  /** Query parameters. Arrays become repeated `key[]=value` pairs, Canvas-style. */
  params?: Record<string, string | number | boolean | undefined | Array<string | number>>;
  body?: BodyInit | null;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skip the Authorization header. Only used by the health check. */
  anonymous?: boolean;
}

/** Build a full URL from a Canvas API path plus params. */
export function buildUrl(path: string, params?: RequestOptions['params']): string {
  const { baseUrl } = getConfig();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const withPrefix = normalizedPath.startsWith('/api/') ? normalizedPath : `/api/v1${normalizedPath}`;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      // Canvas expects repeated `include[]=a&include[]=b`.
      const arrayKey = key.endsWith('[]') ? key : `${key}[]`;
      for (const entry of value) search.append(arrayKey, String(entry));
    } else {
      search.append(key, String(value));
    }
  }

  const query = search.toString();
  return `${baseUrl}${withPrefix}${query ? `?${query}` : ''}`;
}

/**
 * A tiny concurrency limiter.
 *
 * BUILD_PROMPT: "Do not fan out 40 parallel requests on page load." A course
 * grid with eight courses that each want assignments, submissions, and
 * announcements would do exactly that without this. The cap adapts to the
 * remaining rate-limit quota.
 */
class RequestQueue {
  private active = 0;
  private readonly waiting: Array<() => void> = [];

  async run<T>(task: () => Promise<T>): Promise<T> {
    const limit = rateLimit.concurrency();
    if (this.active >= limit) {
      await new Promise<void>((resolve) => this.waiting.push(resolve));
    }
    this.active += 1;
    try {
      const delay = rateLimit.delayMs();
      if (delay > 0) await sleep(delay);
      return await task();
    } finally {
      this.active -= 1;
      this.waiting.shift()?.();
    }
  }
}

const queue = new RequestQueue();

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function readErrorBody(response: Response): Promise<CanvasErrorBody | string | null> {
  try {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text) as CanvasErrorBody;
    } catch {
      return text;
    }
  } catch {
    return null;
  }
}

/**
 * The single place every Canvas request goes through.
 *
 * Returns the raw `Response` so callers can read pagination and rate-limit
 * headers; `request()` below is the JSON-decoding wrapper most code uses.
 */
export async function rawRequest(url: string, options: RequestOptions = {}): Promise<Response> {
  const method = (options.method ?? 'GET').toUpperCase();

  // Guard first. Nothing is constructed, let alone sent, until this passes.
  assertWriteAllowed(method, url);

  const headers: Record<string, string> = {
    Accept: 'application/json+canvas-string-ids, application/json',
    ...options.headers,
  };

  if (!options.anonymous) {
    const token = resolveToken();
    if (!token) throw new CanvasNoTokenError();
    headers.Authorization = `Bearer ${token}`;
  }

  return queue.run(async () => {
    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body: options.body ?? null,
        signal: options.signal,
        // The token is in a header, never a cookie; sending credentials would
        // only add ambient auth we do not want.
        credentials: 'omit',
        cache: 'no-store',
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      throw new CanvasNetworkError(url, err);
    }

    rateLimit.observe(response.headers);

    if (!response.ok) {
      const body = await readErrorBody(response);
      throw new CanvasHttpError(response.status, response.statusText, url, body);
    }

    return response;
  });
}

/** JSON request. The workhorse. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, options.params);
  const response = await rawRequest(url, options);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Paginated GET. **Every list endpoint must use this.**
 *
 * Canvas paginates via the `Link` header, so a caller that just reads the first
 * response body gets 10 items and no error. See pagination.ts.
 */
export async function getList<T>(path: string, options: RequestOptions = {}): Promise<T[]> {
  const url = buildUrl(path, { per_page: DEFAULT_PER_PAGE, ...options.params });

  const fetchPage = async (pageUrl: string, signal?: AbortSignal): Promise<PageResult<T>> => {
    const response = await rawRequest(pageUrl, { ...options, method: 'GET', signal });
    const items = (await response.json()) as T[];
    return {
      items: Array.isArray(items) ? items : [],
      links: parseLinkHeader(response.headers.get('Link')),
      headers: response.headers,
    };
  };

  return fetchAllPages<T>(url, fetchPage, { signal: options.signal });
}

/* ------------------------------------------------------------------ */
/* Endpoints — users                                                   */
/* ------------------------------------------------------------------ */

export function getSelf(signal?: AbortSignal): Promise<CanvasUser> {
  return request<CanvasUser>('/users/self', { signal });
}

export function getSelfProfile(signal?: AbortSignal): Promise<CanvasUser> {
  return request<CanvasUser>('/users/self/profile', { signal });
}

/** Canvas's per-course card colors, keyed by asset string (`course_123`). */
export function getCustomColors(signal?: AbortSignal): Promise<{ custom_colors: Record<string, string> }> {
  return request('/users/self/colors', { signal });
}

/* ------------------------------------------------------------------ */
/* Endpoints — courses                                                 */
/* ------------------------------------------------------------------ */

export interface CourseListOptions {
  /** 'active' for the Home grid, 'completed' for Archived. */
  enrollmentState?: 'active' | 'completed' | 'invited_or_pending';
  signal?: AbortSignal;
}

export function getCourses(options: CourseListOptions = {}): Promise<CanvasCourse[]> {
  return getList<CanvasCourse>('/courses', {
    params: {
      enrollment_state: options.enrollmentState ?? 'active',
      enrollment_type: 'student',
      include: ['term', 'teachers', 'total_scores', 'course_image', 'public_description'],
      state: ['available', 'completed'],
    },
    signal: options.signal,
  });
}

export function getCourse(courseId: CanvasId, signal?: AbortSignal): Promise<CanvasCourse> {
  return request<CanvasCourse>(`/courses/${courseId}`, {
    params: { include: ['term', 'teachers', 'total_scores', 'syllabus_body', 'public_description'] },
    signal,
  });
}

export function getCourseUsers(
  courseId: CanvasId,
  enrollmentType?: 'student' | 'teacher' | 'ta' | 'observer' | 'designer',
  signal?: AbortSignal,
): Promise<CanvasUser[]> {
  return getList<CanvasUser>(`/courses/${courseId}/users`, {
    params: {
      include: ['enrollments', 'avatar_url', 'email'],
      ...(enrollmentType ? { enrollment_type: [enrollmentType] } : {}),
    },
    signal,
  });
}

export function getEnrollments(
  courseId: CanvasId,
  signal?: AbortSignal,
): Promise<CanvasEnrollment[]> {
  return getList<CanvasEnrollment>(`/courses/${courseId}/enrollments`, {
    params: { user_id: 'self', include: ['grades'] },
    signal,
  });
}

/** Every enrollment across every course — the Grades page's top-level source. */
export function getSelfEnrollments(signal?: AbortSignal): Promise<CanvasEnrollment[]> {
  return getList<CanvasEnrollment>('/users/self/enrollments', {
    params: { include: ['grades'], state: ['active', 'completed'] },
    signal,
  });
}

/* ------------------------------------------------------------------ */
/* Endpoints — assignments and submissions                             */
/* ------------------------------------------------------------------ */

export function getAssignmentGroups(
  courseId: CanvasId,
  signal?: AbortSignal,
): Promise<CanvasAssignmentGroup[]> {
  return getList<CanvasAssignmentGroup>(`/courses/${courseId}/assignment_groups`, {
    params: {
      include: ['assignments', 'submission', 'assignment_visibility'],
      override_assignment_dates: false,
    },
    signal,
  });
}

export function getAssignments(courseId: CanvasId, signal?: AbortSignal): Promise<CanvasAssignment[]> {
  return getList<CanvasAssignment>(`/courses/${courseId}/assignments`, {
    params: { include: ['submission', 'all_dates', 'score_statistics'], order_by: 'due_at' },
    signal,
  });
}

export function getAssignment(
  courseId: CanvasId,
  assignmentId: CanvasId,
  signal?: AbortSignal,
): Promise<CanvasAssignment> {
  return request<CanvasAssignment>(`/courses/${courseId}/assignments/${assignmentId}`, {
    params: { include: ['submission', 'all_dates', 'rubric_assessment'] },
    signal,
  });
}

export function getSubmission(
  courseId: CanvasId,
  assignmentId: CanvasId,
  signal?: AbortSignal,
): Promise<CanvasSubmission> {
  return request<CanvasSubmission>(
    `/courses/${courseId}/assignments/${assignmentId}/submissions/self`,
    { params: { include: ['submission_comments', 'submission_history', 'rubric_assessment'] }, signal },
  );
}

/** Every submission for the student in one course — the Grades table's source. */
export function getCourseSubmissions(
  courseId: CanvasId,
  signal?: AbortSignal,
): Promise<CanvasSubmission[]> {
  return getList<CanvasSubmission>(`/courses/${courseId}/students/submissions`, {
    params: { student_ids: ['self'], include: ['assignment'], per_page: DEFAULT_PER_PAGE },
    signal,
  });
}

/* ------------------------------------------------------------------ */
/* Endpoints — modules, pages, files                                   */
/* ------------------------------------------------------------------ */

export function getModules(courseId: CanvasId, signal?: AbortSignal): Promise<CanvasModule[]> {
  return getList<CanvasModule>(`/courses/${courseId}/modules`, {
    params: { include: ['items', 'content_details'] },
    signal,
  });
}

export function getModuleItems(
  courseId: CanvasId,
  moduleId: CanvasId,
  signal?: AbortSignal,
): Promise<CanvasModuleItem[]> {
  return getList<CanvasModuleItem>(`/courses/${courseId}/modules/${moduleId}/items`, {
    params: { include: ['content_details'] },
    signal,
  });
}

export function getFiles(courseId: CanvasId, signal?: AbortSignal): Promise<CanvasFile[]> {
  return getList<CanvasFile>(`/courses/${courseId}/files`, {
    params: { sort: 'created_at', order: 'desc' },
    signal,
  });
}

export function getFolders(courseId: CanvasId, signal?: AbortSignal): Promise<CanvasFolder[]> {
  return getList<CanvasFolder>(`/courses/${courseId}/folders`, { signal });
}

export function getPages(courseId: CanvasId, signal?: AbortSignal): Promise<CanvasPage[]> {
  return getList<CanvasPage>(`/courses/${courseId}/pages`, { signal });
}

export function getPage(
  courseId: CanvasId,
  pageUrl: string,
  signal?: AbortSignal,
): Promise<CanvasPage> {
  return request<CanvasPage>(`/courses/${courseId}/pages/${encodeURIComponent(pageUrl)}`, { signal });
}

/* ------------------------------------------------------------------ */
/* Endpoints — discussions, announcements, quizzes                     */
/* ------------------------------------------------------------------ */

export function getDiscussions(
  courseId: CanvasId,
  signal?: AbortSignal,
): Promise<CanvasDiscussionTopic[]> {
  return getList<CanvasDiscussionTopic>(`/courses/${courseId}/discussion_topics`, {
    params: { exclude_announcements: true },
    signal,
  });
}

export function getDiscussion(
  courseId: CanvasId,
  topicId: CanvasId,
  signal?: AbortSignal,
): Promise<CanvasDiscussionTopic> {
  return request<CanvasDiscussionTopic>(`/courses/${courseId}/discussion_topics/${topicId}`, {
    signal,
  });
}

export function getDiscussionEntries(
  courseId: CanvasId,
  topicId: CanvasId,
  signal?: AbortSignal,
): Promise<CanvasDiscussionEntry[]> {
  return getList<CanvasDiscussionEntry>(
    `/courses/${courseId}/discussion_topics/${topicId}/entries`,
    { signal },
  );
}

/** Announcements are discussion topics; Canvas exposes them on their own route. */
export function getAnnouncements(
  courseIds: CanvasId[],
  signal?: AbortSignal,
): Promise<CanvasDiscussionTopic[]> {
  if (courseIds.length === 0) return Promise.resolve([]);
  return getList<CanvasDiscussionTopic>('/announcements', {
    params: { context_codes: courseIds.map((id) => `course_${id}`), active_only: true },
    signal,
  });
}

export function getQuizzes(courseId: CanvasId, signal?: AbortSignal): Promise<CanvasQuiz[]> {
  return getList<CanvasQuiz>(`/courses/${courseId}/quizzes`, { signal });
}

/* ------------------------------------------------------------------ */
/* Endpoints — stream, to-do, planner, calendar                        */
/* ------------------------------------------------------------------ */

export function getActivityStream(signal?: AbortSignal): Promise<CanvasActivityItem[]> {
  return getList<CanvasActivityItem>('/users/self/activity_stream', { signal });
}

export function getTodo(signal?: AbortSignal): Promise<CanvasTodoItem[]> {
  return getList<CanvasTodoItem>('/users/self/todo', { signal });
}

export interface PlannerOptions {
  startDate?: string;
  endDate?: string;
  contextCodes?: string[];
  signal?: AbortSignal;
}

export function getPlannerItems(options: PlannerOptions = {}): Promise<CanvasPlannerItem[]> {
  return getList<CanvasPlannerItem>('/planner/items', {
    params: {
      ...(options.startDate ? { start_date: options.startDate } : {}),
      ...(options.endDate ? { end_date: options.endDate } : {}),
      ...(options.contextCodes ? { context_codes: options.contextCodes } : {}),
    },
    signal: options.signal,
  });
}

export interface CalendarOptions {
  startDate: string;
  endDate: string;
  contextCodes: string[];
  /** 'event' for real events, 'assignment' for due dates. Fetch both. */
  type?: 'event' | 'assignment';
  signal?: AbortSignal;
}

export function getCalendarEvents(options: CalendarOptions): Promise<CanvasCalendarEvent[]> {
  return getList<CanvasCalendarEvent>('/calendar_events', {
    params: {
      start_date: options.startDate,
      end_date: options.endDate,
      context_codes: options.contextCodes,
      type: options.type ?? 'event',
      per_page: DEFAULT_PER_PAGE,
    },
    signal: options.signal,
  });
}

/* ------------------------------------------------------------------ */
/* Health                                                              */
/* ------------------------------------------------------------------ */

export interface ProxyHealth {
  ok: boolean;
  ts: number;
  version?: string;
  canvasConfigured?: boolean;
  clientKeyRequired?: boolean;
}

/** Unauthenticated liveness check against the worker. Used by Settings. */
export async function checkProxyHealth(signal?: AbortSignal): Promise<ProxyHealth> {
  const { baseUrl } = getConfig();
  const response = await rawRequest(`${baseUrl}/health`, { anonymous: true, signal });
  return (await response.json()) as ProxyHealth;
}

/**
 * "Test connection" for the Settings screen: does this token actually work?
 * Read-only, one request, exactly like probe.html check 3.
 */
export async function verifyToken(signal?: AbortSignal): Promise<CanvasUser> {
  return getSelf(signal);
}
